// Ranked Discovery view (Phase 5D slice 2).
//
// Lives at /<project_slug>/discover/. The user picks a saved profile,
// clicks "Run Discovery", and the view fetches the project's pinned
// parcels, sends them + the profile slug to /api/deals/discover/, and
// renders a sortable table of ranked candidates.
//
// Future slices:
// - Slice 3: "Pin & Open" creates a real Deal from a candidate's
//   auto-state, redirects to the editor. Bulk: "Pin top N".
// - Phase 5F: ARV badge surfaces basis (assessment / comps / external).
// - Phase 5G: riskFlags column renders icons + tooltips.
//
// Today's candidate source is workspace pins only — most natural MVP
// scope. A future "Use all project parcels" button can extend to the
// project's filtered parcel set without changing the URL or backend.

import axios from 'axios';

import {svc} from '../../request';

const STANDARD_HEADERS = {
	'Accept': 'application/json',
	'Content-Type': 'application/json',
};

const XSRF_CFG = {
	xsrfCookieName: 'csrftoken',
	xsrfHeaderName: 'X-CSRFToken',
};

// Create a persisted Deal from a discovery candidate. The candidate's
// `autoState` carries everything (mode, costs, finance, list/purchase/
// ARV, address, lot acres, parcel pointer); the resulting Deal is
// project-attached (via `projectSlug`) so it lands in the project's
// block graph alongside the existing list/snapshot/print blocks.
async function pinCandidateAsDeal(
	candidate: IDiscoveryCandidate,
	projectSlug: string,
): Promise<{slug: string} | null> {
	try {
		const rv = await axios.request<{slug: string}>({
			method: 'POST',
			url: '/api/deals/',
			headers: STANDARD_HEADERS,
			data: {
				// Title left empty so Phase 5B's per-(parcel, user)
				// auto-name kicks in — discovery-derived deals show
				// up as "Scenario 1", "Scenario 2", etc. The deal-
				// list and editor headers surface the parcel address
				// from `stateJson.property.address`, so the auto-name
				// is what differentiates multiple scenarios on the
				// same parcel.
				title: '',
				parcelUid: candidate.parcel.uid,
				parcelSnapshotJson: candidate.parcel,
				stateJson: candidate.autoState,
				projectSlug,
			},
			responseType: 'json',
			...XSRF_CFG,
		});
		return rv.data;
	} catch {
		return null;
	}
}

type SortField =
	| 'score'
	| 'roi'
	| 'profit'
	| 'cash'
	| 'price'
	| 'arv'
	| 'address';
type SortDirection = 'asc' | 'desc';

export class DealDiscoveryView {
	private rootEl: HTMLElement | null;
	private bodyEl: HTMLElement | null;
	private controlsEl: HTMLElement | null;
	private loadingEl: HTMLElement | null;
	private projectSlug: string;
	private profiles: ReadonlyArray<IDealProfile> = [];
	private parcelUids: ReadonlyArray<string> = [];
	private selectedProfileSlug: string | null = null;
	private response: IDiscoveryResponse | null = null;
	private running: boolean = false;
	private sortField: SortField = 'score';
	private sortDirection: SortDirection = 'desc';

	constructor() {
		this.rootEl = document.getElementById('id_lb-deal-discover');
		this.bodyEl = document.getElementById('id_lb-deal-discover-body');
		this.controlsEl = document.getElementById('id_lb-deal-discover-controls');
		this.loadingEl = document.getElementById('id_lb-deal-discover-loading');
		this.projectSlug = (this.rootEl && this.rootEl.getAttribute('data-project-slug')) || '';
		void this.hydrate();
	}

	private async hydrate(): Promise<void> {
		try {
			const [profilesRv, items] = await Promise.all([
				svc.dealProfile.list(),
				svc.workspaceItem.list(this.projectSlug),
			]);
			this.profiles = profilesRv.profiles;
			this.parcelUids = items
				.filter(i => i.itemType === 'parcel' && i.targetKey)
				.map(i => i.targetKey);
			const defaultProfile = this.profiles.find(p => p.isDefault) || this.profiles[0] || null;
			this.selectedProfileSlug = defaultProfile ? defaultProfile.slug : null;
			this.renderControls();
			this.renderBody();
		} catch {
			this.renderError();
		}
	}

	private renderControls(): void {
		if (!this.controlsEl) {
			return;
		}
		this.controlsEl.textContent = '';
		if (this.profiles.length === 0) {
			const empty = document.createElement('div');
			empty.className = 'lb-deal-discover__hint';
			const link = document.createElement('a');
			link.href = '/deals/profiles/onboarding/';
			link.textContent = 'Create your first profile (5 questions)';
			empty.appendChild(document.createTextNode('No profiles yet. '));
			empty.appendChild(link);
			this.controlsEl.appendChild(empty);
			return;
		}
		const picker = document.createElement('label');
		picker.className = 'lb-deal-discover__picker';
		const lab = document.createElement('span');
		lab.className = 'lb-deal-discover__picker-label';
		lab.textContent = 'Profile';
		picker.appendChild(lab);
		const select = document.createElement('select');
		select.className = 'lb-deal-discover__picker-select';
		this.profiles.forEach(p => {
			const opt = document.createElement('option');
			opt.value = p.slug;
			opt.textContent = `${p.name || p.slug}${p.isDefault ? ' (default)' : ''}`;
			if (this.selectedProfileSlug === p.slug) {
				opt.selected = true;
			}
			select.appendChild(opt);
		});
		select.addEventListener('change', () => {
			this.selectedProfileSlug = select.value;
		});
		picker.appendChild(select);
		this.controlsEl.appendChild(picker);
		const run = document.createElement('button');
		run.type = 'button';
		run.className = 'lb-deal-discover__run';
		run.textContent = this.running ? 'Running…' : 'Run Discovery';
		run.disabled = this.running || !this.selectedProfileSlug || this.parcelUids.length === 0;
		run.addEventListener('click', () => void this.handleRun());
		this.controlsEl.appendChild(run);
	}

	private async handleRun(): Promise<void> {
		if (!this.selectedProfileSlug || this.parcelUids.length === 0) {
			return;
		}
		this.running = true;
		this.renderControls();
		try {
			this.response = await svc.dealDiscovery.run({
				profileSlug: this.selectedProfileSlug,
				parcelUids: this.parcelUids,
				limit: 50,
			});
		} catch {
			this.response = null;
		}
		this.running = false;
		this.renderControls();
		this.renderBody();
	}

	private renderBody(): void {
		if (!this.bodyEl) {
			return;
		}
		this.bodyEl.textContent = '';
		if (this.loadingEl && this.loadingEl.parentNode) {
			this.loadingEl.parentNode.removeChild(this.loadingEl);
		}
		if (this.parcelUids.length === 0) {
			this.bodyEl.appendChild(this.emptyCard(
				'No pinned parcels on this project yet.',
				'Pin some parcels from the Pinned panel, then come back to run discovery against your saved profile.'));
			return;
		}
		if (!this.response) {
			this.bodyEl.appendChild(this.emptyCard(
				'Ready to discover.',
				`${this.parcelUids.length} pinned ${this.parcelUids.length === 1 ? 'parcel' : 'parcels'} will be scored against the selected profile.`));
			return;
		}
		this.bodyEl.appendChild(this.renderSummary());
		const candidates = this.sortedCandidates();
		if (candidates.length === 0) {
			this.bodyEl.appendChild(this.emptyCard(
				'No candidates passed the profile.',
				`${this.response.knockoutCount} of ${this.response.totalCandidates} ${this.response.totalCandidates === 1 ? 'parcel was' : 'parcels were'} knocked out by the profile's filters. Try widening the bands or picking a different profile.`));
			return;
		}
		this.bodyEl.appendChild(this.renderTable(candidates));
	}

	private renderSummary(): HTMLElement {
		const summary = document.createElement('div');
		summary.className = 'lb-deal-discover__summary';
		const rv = this.response!;
		const text = document.createElement('span');
		text.className = 'lb-deal-discover__summary-text';
		const parts: Array<string> = [
			`Ranked ${rv.rankedCount} of ${rv.totalCandidates} pinned ${rv.totalCandidates === 1 ? 'parcel' : 'parcels'}`,
		];
		if (rv.knockoutCount > 0) {
			parts.push(`${rv.knockoutCount} knocked out by profile filters`);
		}
		parts.push(`Profile: ${rv.profile.name || rv.profile.slug}`);
		text.textContent = parts.join(' · ');
		summary.appendChild(text);
		// Bulk "Pin top N" — creates persisted Deals for the top N
		// ranked candidates so the user can compare or print them all
		// without clicking through each row. Capped at min(5, ranked)
		// because pinning everything by default is too aggressive.
		if (rv.candidates.length > 0) {
			const cap = Math.min(5, rv.candidates.length);
			const pinTop = document.createElement('button');
			pinTop.type = 'button';
			pinTop.className = 'lb-deal-discover__bulk-btn';
			pinTop.textContent = this.bulkPinning ? `Pinning top ${cap}…` : `Pin top ${cap}`;
			pinTop.disabled = this.bulkPinning;
			pinTop.addEventListener('click', () => void this.handleBulkPin(cap));
			summary.appendChild(pinTop);
		}
		return summary;
	}

	private bulkPinning: boolean = false;

	private async handleBulkPin(n: number): Promise<void> {
		if (!this.response || this.bulkPinning) {
			return;
		}
		const top = this.sortedCandidates().slice(0, n);
		if (top.length === 0) {
			return;
		}
		this.bulkPinning = true;
		this.renderBody();
		const created = await Promise.all(
			top.map(c => pinCandidateAsDeal(c, this.projectSlug))
		);
		const okCount = created.filter(d => d !== null).length;
		this.bulkPinning = false;
		// Land on the project-scoped deals list so the user sees all
		// the new scenarios in one place. Partial failures are visible
		// there (the user can re-discover and pin any that didn't
		// land).
		if (okCount > 0) {
			window.location.assign(`/${encodeURIComponent(this.projectSlug)}/deals/`);
		} else {
			this.renderBody();
		}
	}

	private sortedCandidates(): ReadonlyArray<IDiscoveryCandidate> {
		const rows = (this.response?.candidates || []).slice();
		const key = this.sortField;
		const dir = this.sortDirection === 'desc' ? -1 : 1;
		const value = (c: IDiscoveryCandidate): number | string => {
			const m = c.metrics || {};
			switch (key) {
				case 'roi': return Number(m.projectedROI || 0);
				case 'profit': return Number(m.projectedProfit || 0);
				case 'cash': return Number(m.totalCashInDeal || 0);
				case 'price': return Number(c.parcel.totalValue || 0);
				case 'arv': return Number(m.adjustedARV || 0);
				case 'address': return String(c.parcel.streetAddress || '');
				case 'score':
				default: return Number(c.score || 0);
			}
		};
		rows.sort((a, b) => {
			const va = value(a);
			const vb = value(b);
			if (va < vb) return -1 * dir;
			if (va > vb) return 1 * dir;
			return 0;
		});
		return rows;
	}

	private setSort(field: SortField): void {
		if (this.sortField === field) {
			this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
		} else {
			this.sortField = field;
			// Default: higher-is-better → desc; address → asc.
			this.sortDirection = field === 'address' || field === 'cash' || field === 'price' ? 'asc' : 'desc';
		}
		this.renderBody();
	}

	private renderTable(rows: ReadonlyArray<IDiscoveryCandidate>): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-discover__table-wrap';
		const table = document.createElement('table');
		table.className = 'lb-deal-discover__table';
		const thead = document.createElement('thead');
		const headRow = document.createElement('tr');
		const cols: Array<[SortField, string]> = [
			['address', 'Address'],
			['price', 'Asmt. value'],
			['arv', 'ARV est.'],
			['roi', 'ROI'],
			['profit', 'Profit'],
			['cash', 'Cash in'],
			['score', 'Score'],
		];
		cols.forEach(([field, label]) => {
			const th = document.createElement('th');
			th.className = 'lb-deal-discover__th';
			if (this.sortField === field) {
				th.classList.add(`lb-deal-discover__th--sort-${this.sortDirection}`);
			}
			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'lb-deal-discover__th-btn';
			btn.textContent = label;
			btn.addEventListener('click', () => this.setSort(field));
			th.appendChild(btn);
			headRow.appendChild(th);
		});
		// Grade column at the right — not sortable for now (tier-only).
		const gradeTh = document.createElement('th');
		gradeTh.className = 'lb-deal-discover__th';
		gradeTh.textContent = 'Grade';
		headRow.appendChild(gradeTh);
		thead.appendChild(headRow);
		table.appendChild(thead);
		const tbody = document.createElement('tbody');
		rows.forEach(c => tbody.appendChild(this.renderRow(c)));
		table.appendChild(tbody);
		wrap.appendChild(table);
		return wrap;
	}

	private renderRow(candidate: IDiscoveryCandidate): HTMLElement {
		const tr = document.createElement('tr');
		tr.className = 'lb-deal-discover__row';
		const m = candidate.metrics || {};
		tr.appendChild(this.addressCell(candidate));
		tr.appendChild(this.moneyCell(candidate.parcel.totalValue));
		tr.appendChild(this.moneyCell(m.adjustedARV));
		tr.appendChild(this.pctCell(m.projectedROI));
		tr.appendChild(this.moneyCell(m.projectedProfit, true));
		tr.appendChild(this.moneyCell(m.totalCashInDeal));
		tr.appendChild(this.scoreCell(candidate.score));
		tr.appendChild(this.gradeCell(candidate.grade));
		// Phase 5D slice 3 will replace the whole-row click with a Pin
		// & Open action that creates a real Deal. For now: clicking the
		// address opens a new-deal flow with the parcel pre-populated.
		return tr;
	}

	private addressCell(c: IDiscoveryCandidate): HTMLElement {
		const td = document.createElement('td');
		td.className = 'lb-deal-discover__td lb-deal-discover__td--address';
		// Phase 5D slice 3: clicking the address pins the candidate as
		// a real persisted Deal (with full auto-state — mode, costs,
		// list/purchase/ARV, parcel snapshot) and navigates to the
		// project-scoped editor. Lazy-pin keeps discovery itself
		// read-only; users only pay the persistence cost for
		// candidates they explicitly act on.
		const link = document.createElement('a');
		link.className = 'lb-deal-discover__address-link';
		link.href = '#';  // overridden by the click handler
		link.textContent = c.parcel.streetAddress || c.parcel.uid.slice(0, 8);
		link.addEventListener('click', evt => {
			evt.preventDefault();
			void this.handlePinAndOpen(c, link);
		});
		td.appendChild(link);
		if (c.whyRanked && c.whyRanked.length > 0) {
			const meta = document.createElement('span');
			meta.className = 'lb-deal-discover__row-meta';
			meta.textContent = c.whyRanked[0];
			td.appendChild(meta);
		}
		return td;
	}

	private async handlePinAndOpen(c: IDiscoveryCandidate, link: HTMLElement): Promise<void> {
		const original = link.textContent;
		link.textContent = 'Pinning…';
		try {
			const created = await pinCandidateAsDeal(c, this.projectSlug);
			if (created && created.slug) {
				window.location.assign(
					`/${encodeURIComponent(this.projectSlug)}/deals/${encodeURIComponent(created.slug)}/`,
				);
				return;
			}
		} catch {
			// fall through
		}
		// Persistence failed — restore the link and surface inline.
		link.textContent = original || 'Open';
	}

	private moneyCell(value: number | null | undefined, signed: boolean = false): HTMLElement {
		const td = document.createElement('td');
		td.className = 'lb-deal-discover__td lb-deal-discover__td--number';
		const n = Number(value || 0);
		if (signed) {
			if (n > 0) td.classList.add('lb-deal-discover__td--positive');
			else if (n < 0) td.classList.add('lb-deal-discover__td--negative');
		}
		td.textContent = formatCurrency(n);
		return td;
	}

	private pctCell(value: number | null | undefined): HTMLElement {
		const td = document.createElement('td');
		td.className = 'lb-deal-discover__td lb-deal-discover__td--number';
		const n = Number(value || 0);
		if (n > 0) td.classList.add('lb-deal-discover__td--positive');
		else if (n < 0) td.classList.add('lb-deal-discover__td--negative');
		td.textContent = `${n.toFixed(1)}%`;
		return td;
	}

	private scoreCell(value: number): HTMLElement {
		const td = document.createElement('td');
		td.className = 'lb-deal-discover__td lb-deal-discover__td--number';
		td.textContent = value.toFixed(3);
		return td;
	}

	private gradeCell(grade: IDiscoveryGrade): HTMLElement {
		const td = document.createElement('td');
		td.className = 'lb-deal-discover__td';
		const chip = document.createElement('span');
		chip.className = `lb-deal-discover__grade lb-deal-discover__grade--${grade.color || 'neutral'}`;
		chip.textContent = grade.label || '—';
		td.appendChild(chip);
		return td;
	}

	private emptyCard(title: string, body: string): HTMLElement {
		const empty = document.createElement('div');
		empty.className = 'lb-deal-discover__empty';
		const strong = document.createElement('strong');
		strong.textContent = title;
		const span = document.createElement('span');
		span.textContent = body;
		empty.appendChild(strong);
		empty.appendChild(span);
		return empty;
	}

	private renderError(): void {
		if (!this.bodyEl) {
			return;
		}
		this.bodyEl.textContent = '';
		this.bodyEl.appendChild(this.emptyCard(
			'Could not load discovery.',
			'Profiles or workspace items failed to fetch. Try refreshing.'));
	}
}

function formatCurrency(n: number): string {
	if (!Number.isFinite(n)) {
		return '$0';
	}
	return n.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});
}
